import React, { useState } from 'react';
import { CommentInput } from './CommentInput';
import type { ProgramExercise, WorkoutSet } from '../types';

interface SetInputFormProps {
  onAddSet: (setData: Omit<WorkoutSet, 'id' | 'exercise_id' | 'set_number' | 'completed_at'>) => void;
  currentExercise: ProgramExercise;
  currentSetNumber: number;
  previousSet?: WorkoutSet; // Previous set for auto-fill
}

/**
 * SetInputForm - Adaptive form for different exercise types
 * Supports weight+reps, bodyweight, time-based, and distance exercises
 */
export const SetInputForm: React.FC<SetInputFormProps> = ({ 
  onAddSet, 
  currentExercise, 
  currentSetNumber,
  previousSet
}) => {
  // Auto-fill from previous set if available
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [duration, setDuration] = useState('');
  const [distance, setDistance] = useState('');
  const [bodyweightModifier, setBodyweightModifier] = useState('');
  const [assistance, setAssistance] = useState('');
  const [notes, setNotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [lastExerciseId, setLastExerciseId] = useState(currentExercise.exercise.id);

  // Reset form when exercise changes
  React.useEffect(() => {
    if (currentExercise.exercise.id !== lastExerciseId) {
      // Exercise changed - reset form completely
      setWeight('');
      setReps('');
      setDuration('');
      setDistance('');
      setBodyweightModifier('');
      setAssistance('');
      setNotes('');
      setShowAdvanced(false);
      setIsFormDirty(false);
      setLastExerciseId(currentExercise.exercise.id);
    }
  }, [currentExercise.exercise.id, lastExerciseId]);

  // Update fields when previousSet changes (auto-fill)
  React.useEffect(() => {
    // Only auto-fill if:
    // 1. There is a previous set
    // 2. Form is not dirty (user hasn't started typing)
    // 3. Current set number is > 1 (not the first set of an exercise)
    if (previousSet && !isFormDirty && currentSetNumber > 1) {
      setWeight(previousSet.weight?.toString() || '');
      setReps(previousSet.reps?.toString() || '');
      setDuration(previousSet.duration_seconds?.toString() || '');
      setDistance(previousSet.distance_meters?.toString() || '');
      setBodyweightModifier(previousSet.bodyweight_modifier || '');
      setAssistance(previousSet.assistance || '');
      // Don't auto-fill notes - they should be unique per set
    }
  }, [previousSet, isFormDirty, currentSetNumber]);

  const exerciseType = currentExercise.exercise.exercise_type || 'вес_повторения';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const setData: Omit<WorkoutSet, 'id' | 'exercise_id' | 'set_number' | 'completed_at'> = {
      notes: notes.trim() || undefined,
      assistance: assistance.trim() || undefined,
      bodyweight_modifier: bodyweightModifier.trim() || undefined
    };

    // Validate and add data based on exercise type
    switch (exerciseType) {
      case 'вес_повторения':
        const weightNum = parseFloat(weight);
        const repsNum = parseInt(reps);
        
        if (isNaN(weightNum) || weightNum < 0) {
          alert('Введите корректный вес (0 или больше)');
          return;
        }
        if (isNaN(repsNum) || repsNum <= 0) {
          alert('Введите корректное количество повторений (больше 0)');
          return;
        }
        
        setData.weight = weightNum;
        setData.reps = repsNum;
        break;

      case 'собственный_вес_повторения':
        const bodyweightReps = parseInt(reps);
        
        if (isNaN(bodyweightReps) || bodyweightReps <= 0) {
          alert('Введите корректное количество повторений (больше 0)');
          return;
        }
        
        setData.reps = bodyweightReps;
        // Weight is null for bodyweight exercises
        break;

      case 'на_время':
        const durationNum = parseDurationInput(duration);
        
        if (durationNum <= 0) {
          alert('Введите корректное время (больше 0 секунд)');
          return;
        }
        
        setData.duration_seconds = durationNum;
        break;

      case 'расстояние_время':
        const distanceNum = parseFloat(distance);
        const timeDuration = parseDurationInput(duration);
        
        if (isNaN(distanceNum) || distanceNum <= 0) {
          alert('Введите корректное расстояние (больше 0)');
          return;
        }
        if (timeDuration <= 0) {
          alert('Введите корректное время (больше 0 секунд)');
          return;
        }
        
        setData.distance_meters = distanceNum;
        setData.duration_seconds = timeDuration;
        break;

      case 'только_повторения':
        const repsOnlyNum = parseInt(reps);
        
        if (isNaN(repsOnlyNum) || repsOnlyNum <= 0) {
          alert('Введите корректное количество повторений (больше 0)');
          return;
        }
        
        setData.reps = repsOnlyNum;
        break;

      default:
        alert('Неизвестный тип упражнения');
        return;
    }

    onAddSet(setData);

    // Mark form as dirty so auto-fill doesn't override user input
    setIsFormDirty(false);
    
    // Reset only notes and advanced options
    // Weight, reps, etc. will be auto-filled from the set we just added
    setNotes('');
    setShowAdvanced(false);
  };

  const parseDurationInput = (input: string): number => {
    // Parse formats like "1:30", "90", "1м 30с"
    if (input.includes(':')) {
      const [mins, secs] = input.split(':').map(n => parseInt(n) || 0);
      return mins * 60 + secs;
    }
    if (input.includes('м')) {
      const mins = parseInt(input.match(/(\d+)м/)?.[1] || '0');
      const secs = parseInt(input.match(/(\d+)с/)?.[1] || '0');
      return mins * 60 + secs;
    }
    return parseInt(input) || 0;
  };

  const handleDurationChange = (value: string) => {
    setDuration(value);
    // Auto-convert common formats
    const seconds = parseDurationInput(value);
    if (seconds > 0 && value !== seconds.toString()) {
      // Show formatted version as placeholder or hint
    }
  };

  const renderInputFields = () => {
    switch (exerciseType) {
      case 'вес_повторения':
        return (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Вес (кг) <span className="text-gray-500">*</span>
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={weight}
                onChange={(e) => {
                  setWeight(e.target.value);
                  setIsFormDirty(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0 для собств. веса"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Повторения <span className="text-gray-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={reps}
                onChange={(e) => {
                  setReps(e.target.value);
                  setIsFormDirty(true);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="10"
                required
              />
            </div>
          </div>
        );

      case 'собственный_вес_повторения':
        return (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Повторения <span className="text-gray-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="20"
              required
            />
            {showAdvanced && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Модификация
                </label>
                <input
                  type="text"
                  value={bodyweightModifier}
                  onChange={(e) => setBodyweightModifier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ноги на возвышении, одной рукой"
                />
              </div>
            )}
          </div>
        );

      case 'на_время':
        return (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Время <span className="text-gray-500">*</span>
            </label>
            <input
              type="text"
              value={duration}
              onChange={(e) => handleDurationChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="1:30 или 90 (секунд)"
              required
            />
            <div className="text-xs text-gray-500 mt-1">
              Форматы: 1:30, 90с, 1м 30с
            </div>
          </div>
        );

      case 'расстояние_время':
        return (
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Расстояние (м) <span className="text-gray-500">*</span>
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="1000"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Время <span className="text-gray-500">*</span>
              </label>
              <input
                type="text"
                value={duration}
                onChange={(e) => handleDurationChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="5:30"
                required
              />
            </div>
          </div>
        );

      case 'только_повторения':
        return (
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Количество <span className="text-gray-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="50"
              required
            />
          </div>
        );

      default:
        return null;
    }
  };

  const getExerciseTypeLabel = () => {
    switch (exerciseType) {
      case 'вес_повторения': return 'Вес + Повторения';
      case 'собственный_вес_повторения': return 'Собственный вес';
      case 'на_время': return 'На время';
      case 'расстояние_время': return 'Расстояние + Время';
      case 'только_повторения': return 'Только количество';
      default: return 'Стандартное';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium">Добавить подход #{currentSetNumber}</h3>
        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
          {getExerciseTypeLabel()}
        </span>
      </div>
      
      {renderInputFields()}

      {/* Advanced Options */}
      <div className="mb-3">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {showAdvanced ? '▼' : '▶'} Дополнительно
        </button>
      </div>

      {showAdvanced && (
        <div className="space-y-3 mb-3">
          {exerciseType === 'вес_повторения' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Помощь (гравитон, резинка)
              </label>
              <input
                type="text"
                value={assistance}
                onChange={(e) => setAssistance(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="-10кг гравитон"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Заметки к подходу
            </label>
            <CommentInput
              value={notes}
              onChange={setNotes}
              placeholder="Тяжело, в запасе"
              type="set"
              context={{
                exerciseType: currentExercise.exercise.name_ru,
                weight: parseFloat(weight) || 0,
                reps: parseInt(reps) || 0,
                previousComments: []
              }}
            />
          </div>
        </div>
      )}

      <button
        type="submit"
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700"
      >
        Добавить подход
      </button>
    </form>
  );
};

export default SetInputForm;